import { useState } from 'react';
import { useI18n } from '../../i18n';
import { useSEO } from '../../hooks/useSEO';

export default function HTMLEntityEncoder() {
    const { t } = useI18n();
    const [input, setInput] = useState('');
    const [output, setOutput] = useState('');

    useSEO({
        title: 'HTML Entity Encoder/Decoder',
        description: 'Encode and decode HTML entities and special characters',
        keywords: ['html', 'entity', 'encoder', 'decoder', 'escape', 'unescape', 'special characters'],
    });

    const encode = () => {
        const textarea = document.createElement('textarea');
        textarea.textContent = input;
        const encoded = textarea.innerHTML
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;')
            .replace(/\//g, '&#x2F;');
        setOutput(encoded);
    };

    const decode = () => {
        const textarea = document.createElement('textarea');
        textarea.innerHTML = input;
        setOutput(textarea.textContent || '');
    };

    const encodeAll = () => {
        let encoded = '';
        for (let i = 0; i < input.length; i++) {
            const char = input[i];
            if (char && char.charCodeAt(0) > 127 || char === '<' || char === '>' || char === '&' || char === '"' || char === "'") {
                encoded += '&#' + char.charCodeAt(0) + ';';
            } else {
                encoded += char;
            }
        }
        setOutput(encoded);
    };

    const copyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(output);
            alert(t.common.copiedToClipboard);
        } catch (err) {
            alert(t.common.failedToCopy);
        }
    };

    const commonEntities = [
        { char: '<', name: '&lt;', decimal: '&#60;', desc: 'Less than' },
        { char: '>', name: '&gt;', decimal: '&#62;', desc: 'Greater than' },
        { char: '&', name: '&amp;', decimal: '&#38;', desc: 'Ampersand' },
        { char: '"', name: '&quot;', decimal: '&#34;', desc: 'Quotation mark' },
        { char: "'", name: '&apos;', decimal: '&#39;', desc: 'Apostrophe' },
        { char: ' ', name: '&nbsp;', decimal: '&#160;', desc: 'Non-breaking space' },
        { char: '©', name: '&copy;', decimal: '&#169;', desc: 'Copyright' },
        { char: '®', name: '&reg;', decimal: '&#174;', desc: 'Registered' },
        { char: '™', name: '&trade;', decimal: '&#8482;', desc: 'Trademark' },
        { char: '€', name: '&euro;', decimal: '&#8364;', desc: 'Euro' },
        { char: '£', name: '&pound;', decimal: '&#163;', desc: 'Pound' },
        { char: '¥', name: '&yen;', decimal: '&#165;', desc: 'Yen' },
    ];

    return (
        <div>
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    HTML Entity Encoder/Decoder
                </h1>
                <p className="text-gray-600">
                    Encode and decode HTML entities and special characters
                </p>
            </div>

            <div className="space-y-6">
                {/* Input */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t.common.input}
                    </label>
                    <textarea
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder="Enter text or HTML entities..."
                        className="input-field font-mono text-sm"
                        rows={6}
                    />
                </div>

                {/* Action Buttons */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <button onClick={encode} className="btn-primary">
                        ⬇️ Encode (Basic)
                    </button>
                    <button onClick={encodeAll} className="btn-primary">
                        ⬇️⬇️ Encode (All)
                    </button>
                    <button onClick={decode} className="btn-secondary">
                        ⬆️ Decode
                    </button>
                </div>

                {/* Output */}
                {output && (
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label className="block text-sm font-medium text-gray-700">
                                {t.common.output}
                            </label>
                            <button onClick={copyToClipboard} className="btn-secondary text-sm">
                                {t.common.copy}
                            </button>
                        </div>
                        <textarea
                            value={output}
                            readOnly
                            className="input-field font-mono text-sm bg-gray-50"
                            rows={6}
                        />
                    </div>
                )}

                {/* Common Entities Reference */}
                <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                    <h3 className="font-semibold text-primary-900 mb-3">
                        💡 Common HTML Entities
                    </h3>
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead>
                                <tr className="border-b border-primary-200">
                                    <th className="text-left py-2 px-3 text-primary-900">Character</th>
                                    <th className="text-left py-2 px-3 text-primary-900">Named</th>
                                    <th className="text-left py-2 px-3 text-primary-900">Decimal</th>
                                    <th className="text-left py-2 px-3 text-primary-900">Description</th>
                                </tr>
                            </thead>
                            <tbody className="text-primary-800">
                                {commonEntities.map((entity, i) => (
                                    <tr key={i} className="border-b border-primary-100 hover:bg-primary-100">
                                        <td className="py-2 px-3 font-mono font-bold">{entity.char}</td>
                                        <td className="py-2 px-3 font-mono">
                                            <code className="bg-white px-1 rounded">{entity.name}</code>
                                        </td>
                                        <td className="py-2 px-3 font-mono">
                                            <code className="bg-white px-1 rounded">{entity.decimal}</code>
                                        </td>
                                        <td className="py-2 px-3">{entity.desc}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Encoding Methods */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-3">
                        🔧 Encoding Methods
                    </h3>
                    <div className="space-y-3 text-sm text-gray-700">
                        <div>
                            <div className="font-medium text-gray-900 mb-1">Basic Encoding</div>
                            <p>
                                Encodes only essential HTML characters: <code className="bg-white px-1 rounded">&lt; &gt; &amp; " '</code>
                            </p>
                            <p className="text-gray-600 mt-1">
                                Use for: Regular HTML content, preventing XSS attacks
                            </p>
                        </div>
                        <div>
                            <div className="font-medium text-gray-900 mb-1">Encode All</div>
                            <p>
                                Encodes all special characters and non-ASCII characters as numeric entities
                            </p>
                            <p className="text-gray-600 mt-1">
                                Use for: Maximum compatibility, international characters, ensuring clean HTML
                            </p>
                        </div>
                        <div>
                            <div className="font-medium text-gray-900 mb-1">Decode</div>
                            <p>
                                Converts HTML entities back to their original characters
                            </p>
                            <p className="text-gray-600 mt-1">
                                Use for: Reading encoded content, processing HTML output
                            </p>
                        </div>
                    </div>
                </div>

                {/* Use Cases */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h3 className="font-semibold text-blue-900 mb-2">
                        📚 Common Use Cases
                    </h3>
                    <div className="space-y-2 text-sm text-blue-800">
                        <p>• <strong>Prevent XSS attacks:</strong> Encode user input before displaying in HTML</p>
                        <p>• <strong>Display code:</strong> Show HTML/XML code as text on web pages</p>
                        <p>• <strong>International characters:</strong> Ensure compatibility across different encodings</p>
                        <p>• <strong>Special symbols:</strong> Display copyright, trademark, currency symbols</p>
                        <p>• <strong>Email templates:</strong> Encode special characters in HTML emails</p>
                    </div>
                </div>
            </div>
        </div>
    );
}
